import { getLinePrice } from '@portal/analytics/events/ecommerce/utils';
import { SHOPIFY_STORE_BRAND } from '@portal/config/base';
import { type EcommerceItem, type EcommerceItemCategory } from 'analytics/events/ecommerce';
import type { CartLine, DiscountCode } from 'api/shopifyV2/types';
import { StoreEnum } from 'schema/storeCode';
import { FREQUENCY_INTERVAL } from '@portal/shared/studio/BuildBundleV3/constants';
import { Product } from 'state/cart';

interface Options {
  products: Product[];
  isGifting: boolean;
  currency: string;
}

export const getGA4ItemFromProductForCart = ({ products, isGifting, currency }: Options) =>
  products.map((productVariant: Product, impressionIndex) => {
    const categoryName = (): EcommerceItemCategory | undefined => {
      if (isGifting || productVariant.type === 'oneTime') {
        return 'OTP';
      }

      if (productVariant.type === 'subscription' && productVariant.quantity === 2) {
        return 'Subscription Double';
      }

      if (productVariant.type === 'subscription') {
        return 'Subscription';
      }

      return undefined;
    };

    return {
      item_checkout_type: 'unified',
      item_id: productVariant.productId,
      item_name: productVariant.name,
      item_brand: productVariant.brand,
      item_category: categoryName(),
      item_category2:
        productVariant.type === 'subscription' ? String(FREQUENCY_INTERVAL) : undefined,
      variant_id: productVariant.variantId,
      price: getLinePrice(productVariant.price, productVariant.quantity),
      quantity: productVariant.quantity,
      discount: productVariant.discount,
      index: impressionIndex + 1,
      currency
    } satisfies EcommerceItem;
  });

type mapCartLinesToGAEventsEcommerceItemsArguments = {
  cartLines: CartLine[];
  currency: string;
  cartDiscountCodes: DiscountCode[];
  store: StoreEnum;
};

export function mapCartLinesToGAEventsEcommerceItems({
  cartLines,
  currency,
  cartDiscountCodes,
  store
}: mapCartLinesToGAEventsEcommerceItemsArguments) {
  return cartLines.map((item: CartLine, index: number) => {
    // parsing the merchandise title to get the item category and
    // frequency
    const itemCategory = item?.merchandise.title.includes('Subscription') ? 'Subscription' : 'OTP';
    const frequency = item?.merchandise.title.replace(/[^\d]/g, '');

    return {
      item_checkout_type: 'unified',
      item_id: item.id,
      item_name: item.merchandise.product.title,
      item_brand: SHOPIFY_STORE_BRAND[store],
      item_category: itemCategory,
      item_category2: itemCategory === 'Subscription' ? frequency : undefined,
      variant_id: item.merchandise.id,
      price: item.merchandise.priceV2.amount,
      quantity: item.quantity,
      discount: cartDiscountCodes[0]?.code,
      index: index + 1,
      currency
    } satisfies EcommerceItem;
  });
}
